import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from './store';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PlayerStats {
  fid?: number;
  username?: string;
  displayName?: string;
  pfpUrl?: string;
  wins: number;
  losses: number;
  ties: number;
  gamesPlayed: number;
  totalPoints: number;
}

export interface ProfileState {
  localStats: Omit<PlayerStats, 'fid' | 'username' | 'displayName' | 'pfpUrl'>;
  leaderboard: PlayerStats[];
  isLoadingLeaderboard: boolean;
}

// Points formula: win=10, tie=3, participation=1
export function calcPoints(wins: number, ties: number, gamesPlayed: number) {
  return wins * 10 + ties * 3 + gamesPlayed;
}

// ─── Initial state ────────────────────────────────────────────────────────────

const initialState: ProfileState = {
  localStats: {
    wins: 0,
    losses: 0,
    ties: 0,
    gamesPlayed: 0,
    totalPoints: 0,
  },
  leaderboard: [],
  isLoadingLeaderboard: false,
};

// ─── Slice ────────────────────────────────────────────────────────────────────

const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    recordWin(state) {
      state.localStats.wins += 1;
      state.localStats.gamesPlayed += 1;
      state.localStats.totalPoints = calcPoints(
        state.localStats.wins,
        state.localStats.ties,
        state.localStats.gamesPlayed
      );
    },
    recordLoss(state) {
      state.localStats.losses += 1;
      state.localStats.gamesPlayed += 1;
      state.localStats.totalPoints = calcPoints(
        state.localStats.wins,
        state.localStats.ties,
        state.localStats.gamesPlayed
      );
    },
    recordTie(state) {
      state.localStats.ties += 1;
      state.localStats.gamesPlayed += 1;
      state.localStats.totalPoints = calcPoints(
        state.localStats.wins,
        state.localStats.ties,
        state.localStats.gamesPlayed
      );
    },
    setLeaderboard(state, action: PayloadAction<PlayerStats[]>) {
      state.leaderboard = action.payload;
      state.isLoadingLeaderboard = false;
    },
    setLoadingLeaderboard(state, action: PayloadAction<boolean>) {
      state.isLoadingLeaderboard = action.payload;
    },
  },
});

export const {
  recordWin,
  recordLoss,
  recordTie,
  setLeaderboard,
  setLoadingLeaderboard,
} = profileSlice.actions;

export const selectLocalStats = (state: RootState) => state.profile.localStats;
export const selectLeaderboard = (state: RootState) => state.profile.leaderboard;
export const selectIsLoadingLeaderboard = (state: RootState) =>
  state.profile.isLoadingLeaderboard;

export const profileReducer = profileSlice.reducer;
